import type { Variants } from "motion/react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { type ReactNode, useEffect, useState } from "react"
import { FocusScope } from "react-aria"
import { Heading as AriaHeading } from "react-aria-components"

import CoachIdle from "@/assets/images/coach/idle.png"
import CoachTalking from "@/assets/images/coach/talking.png"
import CoachThinking from "@/assets/images/coach/thinking.png"
import CoachingContentRenderer from "@/components/chat/CoachingContentRenderer"
import TypingIndicator from "@/components/common/typing-indicator"
import { DUR, EASE_EMPHASIZED } from "@/constants/coaching-motion"
import type { CoachingContent } from "@/utils/conversation-state-builder"

// Enhanced types for extensibility
export interface CoachingPrompt {
  id: string
  label: string
  type?: "default" | "primary" | "secondary"
  disabled?: boolean
  requiresInput?: boolean
}

type CoachingMode = "guidance" | "question" | "interrupt"

// The coach's three responsive poses. The pose is derived from the surface's own
// signals (talk-then-settle): `thinking` while a line is being prepared, a brief
// `talking` beat as each line lands, then back to `idle`. Callers may override
// explicitly via the `avatarState` prop once richer (e.g. interrupt) triggers
// exist.
type CoachAvatarState = "idle" | "talking" | "thinking"
const COACH_AVATARS: Record<CoachAvatarState, string> = {
  idle: CoachIdle,
  talking: CoachTalking,
  thinking: CoachThinking,
}

// How long the `talking` pose holds after a line is delivered before settling.
const TALK_SETTLE_MS = 1500

interface CoachingSurfaceProps {
  userName: string
  onOptionSelected?: (option: CoachingPrompt) => void
  onCoachingComplete?: () => void
  // A universal escape hatch: when the surface is expanded but has no actionable
  // content (e.g. an idle mid-conversation "Tap for help" with no live coaching
  // or ambient brief), this collapses back to the conversation so no expanded
  // state can ever trap the trainee. The parent passes it only once there is a
  // conversation to return to. Superseded by the Round-2 help panel (helpMode).
  onDismiss?: () => void
  isExpanded?: boolean
  isFocused?: boolean
  onFocus?: () => void
  currentStep?: number
  totalSteps?: number
  // When true, a multi-page framing run renders as a focus-trapping full-screen
  // takeover (a frosted overlay over the blurred conversation) instead of the
  // inline band. Only the parent's pre-conversation framing phase sets this; it
  // must never be set for a mid-conversation interrupt.
  isTakeover?: boolean
  // Optional content the parent renders inside the takeover card (e.g. the doors
  // tracker, so the gradual reveal sits with the lesson). Ignored when not in
  // takeover.
  children?: ReactNode

  // Enhanced props for extensibility
  question?: string
  prompts?: CoachingPrompt[]
  mode?: CoachingMode
  showTypingIndicator?: boolean
  // Explicitly drive the coach's pose. When omitted, the pose is derived from the
  // surface's own loading/speaking signals (talk-then-settle). An explicit
  // `avatarSrc` still wins over both.
  avatarState?: CoachAvatarState
  avatarSrc?: string
  allowSkip?: boolean
  onSkip?: () => void

  // Multi-page plain-content pagination (live path). When a caller drives more
  // than one page and provides onNext, the surface shows "Continue" until the
  // last page, then the usual "Got It". Absent ⇒ existing single-step behaviour.
  hasNextPage?: boolean
  onNext?: () => void

  // Live coaching content (event-stream driven — see useLiveCoaching). When
  // present, the live (non-readOnly) surface renders this structured content via
  // the shared CoachingContentRenderer plus the Continue/Got-It stepper, instead
  // of the goal-selection question + prompt buttons. This is what makes a framing
  // run / an interrupt paint on the live surface the same way replay paints them.
  content?: CoachingContent | string
  // An ambient (surface:"ambient") brief: pre-populated and revealed only when the
  // trainee taps the minimised surface. `ambientUnread` drives the minimised
  // "Coach has a note" affordance; `onAmbientDismiss` collapses the reveal.
  ambientNote?: CoachingContent | string
  ambientUnread?: boolean
  onAmbientDismiss?: () => void

  // Event emission
  emitEvent?: (
    type: string,
    payload: Record<string, unknown>,
    correlates?: Record<string, unknown>,
  ) => Promise<void>

  // Read-only replay mode
  readOnly?: boolean
  readonlyContent?:
    | string
    | {
        title?: string
        instructions?: string[]
        goals?: { id: string; title: string; description?: string }[]
        tips?: string[]
        examples?: string[]
      }
}

// Animation variants for coordinated transitions - optimized for snappy feel
const expandedContentVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.15, ease: "easeOut" },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
}

const collapsedContentVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.15, ease: "easeOut" },
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.25,
      ease: "easeOut",
      delay: 0.2, // Delay to allow avatar animation to mostly complete
    },
  },
}

const buttonContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0,
    },
  },
}

const buttonVariants: Variants = {
  hidden: { opacity: 0, y: -8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
}

export default function CoachingSurface({
  userName,
  onOptionSelected,
  onCoachingComplete,
  onDismiss,
  isExpanded = true,
  isFocused = true,
  onFocus,
  currentStep = 0,
  totalSteps = 1,
  isTakeover = false,
  children,
  question,
  prompts,
  mode = "guidance",
  showTypingIndicator = false,
  avatarState,
  avatarSrc,
  allowSkip = false,
  onSkip,
  hasNextPage = false,
  onNext,
  content,
  ambientNote,
  ambientUnread = false,
  onAmbientDismiss,
  emitEvent,
  readOnly = false,
  readonlyContent,
}: CoachingSurfaceProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectionStartTime] = useState(Date.now())
  const [followUpShownTime, setFollowUpShownTime] = useState<number>(0)

  // The live surface is content-driven: when a coach_dialogue_page's `content`
  // (a framing page or an interrupt) is present, render it instead of the
  // goal-selection question + prompts. `ambientNote` is shown only when revealed.
  const hasLiveContent = !readOnly && content !== undefined
  const showAmbientReveal =
    !readOnly && content === undefined && ambientNote !== undefined

  // Stamp the moment a coaching card first appears so coaching_acknowledged can
  // carry a meaningful time-to-acknowledge (replaces the old prompt-click stamp).
  useEffect(() => {
    if (hasLiveContent || showAmbientReveal) setFollowUpShownTime(Date.now())
  }, [hasLiveContent, showAmbientReveal])

  // Use userName in default question if provided
  const displayQuestion =
    question ??
    `Hey ${userName.split(" ")[0]}, welcome! This is a safe space to practice talking about your mental health with a care professional. What would you like to focus on today?`

  const handlePromptClick = async (promptId: string) => {
    if (!prompts) return

    const prompt = prompts.find((p) => p.id === promptId)
    if (!prompt) return

    // Emit selection event
    if (emitEvent) {
      await emitEvent("coaching_prompt_selected", {
        prompt_id: promptId,
        prompt_label: prompt.label,
        selection_time_ms: Date.now() - selectionStartTime,
        options_presented: prompts.map((p) => p.id),
        selection_index: prompts.findIndex((p) => p.id === promptId),
      })
    }

    if (prompt) onOptionSelected?.(prompt)
    // Lock the selection. The framing run then arrives as coach_dialogue_page
    // events and the live surface renders it (no hardcoded follow-up).
    setSelectedId(promptId)
  }

  const handleGotItClick = () => {
    // Emit acknowledgment event - use void to explicitly ignore the promise
    void (async () => {
      if (emitEvent) {
        await emitEvent("coaching_acknowledged", {
          stage: mode === "interrupt" ? "interrupt" : "framing",
          time_to_acknowledge_ms: followUpShownTime
            ? Date.now() - followUpShownTime
            : 0,
          page_number: currentStep,
          total_pages: totalSteps,
        })
      }

      onCoachingComplete?.()
    })()
  }

  // Dismissing a revealed ambient brief: collapse back to the conversation. Falls
  // back to the standard complete handler when no dedicated dismiss is provided.
  const handleAmbientDismiss = () => {
    ;(onAmbientDismiss ?? onCoachingComplete)?.()
  }

  const handleSkip = () => {
    // Emit skip event - use void to explicitly ignore the promise
    void (async () => {
      if (emitEvent) {
        await emitEvent("coaching_skipped", {
          skipped_at: "prompt_selection",
          options_presented: prompts ? prompts.map((p) => p.id) : [],
          time_to_skip_ms: Date.now() - selectionStartTime,
          pages_viewed: 0,
          total_pages: totalSteps,
        })
      }

      onSkip?.()
    })()
  }

  // Mode-specific styling
  const getModeStyles = () => {
    switch (mode) {
      case "interrupt":
        return "ring-2 ring-orange-500/20"
      default:
        return ""
    }
  }

  // Talk-then-settle pose derivation. `thinking` wins while a line is being
  // prepared; otherwise each freshly-presented line (prompts arriving as the
  // typing indicator clears, or a coaching card appearing) plays a brief
  // `talking` beat before settling back to `idle`. Replay (readOnly) stays
  // calm/idle.
  const reduceMotion = useReducedMotion()
  const [isSpeaking, setIsSpeaking] = useState(false)
  // Re-fire the talking beat on every page advance, not just when content first
  // appears. `hasLiveContent`/`showAmbientReveal` stay `true` across a framing
  // run's pages, so without `currentStep` the coach would only "speak" once for
  // the whole run; including it makes each Continue land as a fresh utterance.
  useEffect(() => {
    if (readOnly || showTypingIndicator) return
    setIsSpeaking(true)
    const timer = window.setTimeout(() => setIsSpeaking(false), TALK_SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [
    readOnly,
    showTypingIndicator,
    hasLiveContent,
    showAmbientReveal,
    currentStep,
  ])

  const derivedAvatarState: CoachAvatarState = showTypingIndicator
    ? "thinking"
    : isSpeaking
      ? "talking"
      : "idle"
  // Precedence: explicit src override > explicit state prop > derived pose.
  const resolvedAvatarSrc =
    avatarSrc ?? COACH_AVATARS[avatarState ?? derivedAvatarState]

  // The coach portrait: a "breathing" circle (subtle pulse while speaking) with a
  // cross-fading idle↔talking image. Extracted so the inline band and the
  // full-screen takeover render the exact same avatar at different sizes.
  const coachAvatar = (
    <motion.div
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white shadow-sm"
      animate={
        !reduceMotion && isSpeaking ? { scale: [1, 1.015, 1] } : { scale: 1 }
      }
      transition={
        !reduceMotion && isSpeaking
          ? { duration: 1.2, ease: "easeInOut", repeat: Infinity }
          : { duration: 0.28, ease: "easeOut" }
      }
    >
      <AnimatePresence initial={false}>
        <motion.img
          key={resolvedAvatarSrc}
          src={resolvedAvatarSrc}
          alt="AI Coach"
          className="absolute inset-0 h-full w-full object-cover"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: reduceMotion ? 0 : DUR.avatarSwap,
            ease: "easeOut",
          }}
        />
      </AnimatePresence>
    </motion.div>
  )

  // The takeover must never trap the trainee. An always-available skip hands off
  // to the conversation, recording a skip when pages remain (otherwise it is a
  // normal acknowledge via the Got-It path).
  const handleTakeoverExit = () => {
    if (hasNextPage) {
      void emitEvent?.("coaching_skipped", {
        skipped_at: "framing",
        pages_viewed: currentStep + 1,
        total_pages: totalSteps,
      })
      onCoachingComplete?.()
    } else {
      handleGotItClick()
    }
  }

  // Full-screen "takeover": the framing run steps forward over a frosted, blurred
  // conversation. Focus is trapped (FocusScope) and restored on exit; Escape and
  // the skip affordance always exit, so it can never trap. Only reachable for a
  // live framing run (hasLiveContent), never replay.
  if (isTakeover && hasLiveContent) {
    const stepButtonCls =
      "bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-600 rounded-lg px-6 py-2.5 text-base font-semibold text-white shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    return (
      <FocusScope contain restoreFocus autoFocus>
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 px-4 backdrop-blur-sm dark:bg-neutral-950/60"
          role="dialog"
          aria-modal="true"
          aria-label="Coaching intro"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: reduceMotion ? 0 : DUR.scrim,
            ease: "easeOut",
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation()
              handleTakeoverExit()
            }
          }}
        >
          <motion.div
            className="flex w-full max-w-xl flex-col items-center gap-6 rounded-2xl bg-white px-8 py-10 shadow-2xl ring-1 ring-gray-900/5 dark:bg-neutral-900 dark:ring-white/10"
            initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    duration: DUR.card,
                    ease: EASE_EMPHASIZED,
                    delay: DUR.cardDelay,
                  }
            }
          >
            {/* Avatar hero */}
            <div className="h-40 w-40">{coachAvatar}</div>

            {/* The teaching card, cross-fading per page */}
            <div className="min-h-[7rem] w-full">
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={currentStep}
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                  transition={{
                    duration: reduceMotion ? 0 : DUR.page,
                    ease: "easeOut",
                  }}
                >
                  <CoachingContentRenderer content={content} emphasis="hero" />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Children placed by the parent below the card (e.g. the doors
                tracker during the intro). */}
            {children}

            {/* Stepper + progress, then an always-present skip (never trap). */}
            <div className="flex flex-col items-center gap-4">
              {hasNextPage && onNext ? (
                <button onClick={onNext} className={stepButtonCls}>
                  Continue
                </button>
              ) : (
                <button onClick={handleGotItClick} className={stepButtonCls}>
                  Got It
                </button>
              )}

              {totalSteps > 1 && (
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalSteps }).map((_, index) => (
                    <motion.div
                      key={index}
                      animate={{ width: index === currentStep ? 40 : 8 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className={`h-2 rounded-full ${
                        index === currentStep ? "bg-brand-600" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}

              <button
                onClick={handleTakeoverExit}
                className="text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline dark:text-gray-500 dark:hover:text-gray-300"
              >
                Skip the intro
              </button>
            </div>
          </motion.div>
        </motion.div>
      </FocusScope>
    )
  }

  return (
    <div
      className={`relative flex flex-col items-center bg-neutral-50 px-4 transition-all duration-300 ease-in-out dark:bg-neutral-900 ${isExpanded ? "py-8" : "h-32 cursor-pointer justify-center"} ${!isFocused ? "opacity-60" : "opacity-100"} ${getModeStyles()} `}
      onClick={!isFocused ? onFocus : undefined}
      role="region"
      aria-label="Coaching assistant"
      // A live auto-expanding interrupt is scene-breaking, so it announces
      // assertively; ordinary guidance stays polite (D7). Replay (readOnly) keeps
      // polite — the scrubber drives it, so it shouldn't shout.
      aria-live={
        isExpanded
          ? !readOnly && mode === "interrupt"
            ? "assertive"
            : "polite"
          : "off"
      }
    >
      <div className="relative flex w-full max-w-3xl justify-center">
        {/* Avatar container - absolute positioning for smooth layout animation */}
        <motion.div
          initial={false}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className="absolute z-10"
          animate={{
            width: isExpanded ? 112 : 40,
            height: isExpanded ? 112 : 40,
            left: isExpanded ? 0 : "50%", // Further left in expanded state for better spacing
            top: isExpanded ? 0 : "50%",
            x: isExpanded ? 0 : -65, // Closer to text in collapsed state
            y: isExpanded ? 0 : -20, // Center vertically
            scale: isExpanded ? 1 : 1, // No scale for clean transition
            opacity: isExpanded ? 1 : 1, // Full opacity
          }}
        >
          {/* A gentle "breathing" pulse while the coach is speaking reads as a
              living cadence rather than a static portrait — see `coachAvatar`. */}
          {coachAvatar}
        </motion.div>
        <AnimatePresence initial={false}>
          {isExpanded ? (
            // Expanded View - Full coaching interface
            <motion.div
              key="expanded"
              variants={expandedContentVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="flex w-full flex-col gap-5"
            >
              {/* Avatar and Speech Bubble */}
              <div className="flex items-start gap-5">
                {/* Avatar placeholder to maintain layout */}
                <div className="h-28 w-28 flex-shrink-0" />

                {/* Speech Bubble and Progress */}
                <div className="relative flex flex-1 flex-col gap-4">
                  {/* Speech Bubble */}
                  <motion.div className="relative rounded-xl bg-gradient-to-br from-white to-gray-50 px-5 py-3.5 shadow-sm ring-1 ring-gray-900/5">
                    <AnimatePresence initial={false} mode="wait">
                      {readOnly ? (
                        <motion.div
                          key="coach-readonly"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {/* Read-only content rendering (replay) */}
                          <CoachingContentRenderer content={readonlyContent} />
                        </motion.div>
                      ) : hasLiveContent ? (
                        <motion.div
                          key="coach-content"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {/* Live coaching card: a framing page or an interrupt,
                              rendered the same way replay renders it. */}
                          <CoachingContentRenderer content={content} />
                          <div className="mt-3">
                            {hasNextPage && onNext ? (
                              <button
                                onClick={onNext}
                                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-600 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                              >
                                Continue
                              </button>
                            ) : (
                              <button
                                onClick={handleGotItClick}
                                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-600 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                              >
                                Got It
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ) : showAmbientReveal ? (
                        <motion.div
                          key="coach-ambient"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {/* A surface:"ambient" brief, revealed on tap. */}
                          <CoachingContentRenderer content={ambientNote} />
                          <div className="mt-3">
                            <button
                              onClick={handleAmbientDismiss}
                              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-600 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                            >
                              Got It
                            </button>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="coach-question"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          <AriaHeading
                            slot="title"
                            className="text-sm leading-relaxed font-medium text-gray-900"
                          >
                            {displayQuestion}
                          </AriaHeading>

                          {prompts === undefined && onDismiss ? (
                            // Idle, expanded, nothing to act on (the "Tap for
                            // help" lockup case). Offer an exit instead of a
                            // perpetual typing indicator — the surface must
                            // never trap the trainee.
                            <div className="mt-3">
                              <button
                                onClick={onDismiss}
                                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 focus-visible:outline-none"
                              >
                                Back to conversation
                              </button>
                            </div>
                          ) : (
                            showTypingIndicator && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-2"
                              >
                                <TypingIndicator />
                              </motion.div>
                            )
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Progress Dots — for paginated framing runs (live + replay)
                      so the Duolingo-style cards advance visibly. Single-page
                      content (totalSteps 1: goal selection / an interrupt) stays
                      dot-free. */}
                  {totalSteps > 1 && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="flex items-center gap-2"
                    >
                      {Array.from({ length: totalSteps }).map((_, index) => (
                        <motion.div
                          key={index}
                          animate={{
                            width: index === currentStep ? 40 : 8,
                          }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className={`h-2 rounded-full ${
                            index === currentStep
                              ? "bg-brand-600"
                              : "bg-gray-300"
                          }`}
                        />
                      ))}
                    </motion.div>
                  )}

                  {/* Prompt Buttons (goal selection only) - Staggered animation.
                      Nested in the coach's column so they tuck directly under the
                      dialogue bubble as a dialogue-tree sub-branch. Hidden once a
                      coaching card / ambient brief is showing, and in replay.
                      Loading is covered by the question bubble's typing dots. */}
                  <AnimatePresence initial={false}>
                    {!readOnly &&
                      !hasLiveContent &&
                      !showAmbientReveal &&
                      prompts !== undefined && (
                        <motion.div
                          key="prompts"
                          variants={buttonContainerVariants}
                          initial="hidden"
                          animate="visible"
                          exit={{
                            opacity: 0,
                            y: -8,
                            transition: { duration: 0.2 },
                          }}
                          className="flex flex-col gap-3 pl-4"
                        >
                          {prompts.length > 0 ? (
                            prompts.map((prompt) => {
                              const isSelected = selectedId === prompt.id
                              const disabled =
                                selectedId !== null || prompt.disabled
                              return (
                                <motion.button
                                  key={prompt.id}
                                  variants={buttonVariants}
                                  onClick={() =>
                                    void handlePromptClick(prompt.id)
                                  }
                                  disabled={disabled}
                                  whileHover={
                                    disabled ? undefined : { scale: 1.02 }
                                  }
                                  whileTap={
                                    disabled ? undefined : { scale: 0.98 }
                                  }
                                  className={`w-fit rounded-xl border px-5 py-3 text-left text-sm font-medium shadow-sm transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${
                                    isSelected
                                      ? "border-brand-300 bg-brand-50 text-brand-900 ring-brand-300 ring-1"
                                      : disabled
                                        ? "border-gray-100 bg-gray-50 text-gray-400"
                                        : "text-brand-900 hover:border-brand-300 hover:bg-brand-50 focus-visible:ring-brand-600 border-gray-200 bg-white"
                                  } `}
                                >
                                  {prompt.label}
                                </motion.button>
                              )
                            })
                          ) : (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.2 }}
                              className="pl-1 text-sm text-gray-500"
                            >
                              No suggestions available.
                            </motion.div>
                          )}

                          {/* Skip option if allowed */}
                          {allowSkip && !readOnly && (
                            <motion.button
                              variants={buttonVariants}
                              onClick={handleSkip}
                              className="mt-2 w-fit text-sm text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
                            >
                              Skip for now
                            </motion.button>
                          )}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ) : (
            // Collapsed View - Compact header
            <motion.div
              key="collapsed"
              variants={collapsedContentVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              // Anchor the lockup so the avatar placeholder sits exactly under the
              // absolutely-positioned avatar (center − 65px, matching its collapsed
              // `x: -65`). Centering by content would shift with label length, so a
              // longer label ("Coach has a note") crowds the avatar while a short
              // one ("Tap for help") doesn't — anchoring keeps the gap constant.
              className="flex w-full items-center gap-3 pl-[calc(50%-65px)]"
            >
              {/* Avatar placeholder to maintain layout */}
              <div className="h-10 w-10 flex-shrink-0" />

              <div className="flex flex-col gap-1">
                <p
                  className={`text-xs ${
                    ambientUnread
                      ? "text-brand-700 dark:text-brand-300 font-medium"
                      : "text-neutral-600 dark:text-neutral-400"
                  }`}
                >
                  {ambientUnread ? "Coach has a note" : "Tap for help"}
                </p>

                {/* Progress Dots */}
                <motion.div className="flex items-center gap-1">
                  {Array.from({ length: totalSteps }).map((_, index) => (
                    <motion.div
                      key={index}
                      animate={{
                        width: index === currentStep ? 32 : 6,
                      }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className={`h-1.5 rounded-full ${
                        index === currentStep ? "bg-brand-600" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
